/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Pure state derivation for the home-screen time-tracker widget (Android JS renderer + the snapshot builder; the iOS
 * twin lives in `targets/widgets/WidgetSharedData.swift`, `TrackerClocks`).
 *
 * The tracker shows TWO independent clocks that must never be conflated:
 *   - the WORKDAY ("Munkaidő"): the shift's worked time, breaks excluded, own pause state (`/workday/*`);
 *   - the TASK time tracking: the project / work-order timer (`/widgets/timer/*`).
 *
 * Each clock is an explicit block with `status` in `idle | running | paused`:
 *   workday: { status, startedAt, pausedAt, breakSec, workedSecAtSnapshot, ... }
 *   task:    { status, name, projectName, startedAt, elapsedSecAtSnapshot, onBreak }
 * measured at `snapshot.clocksAt` (the SERVER's `generatedAt` of the data the clocks came from). A clock may tick ONLY
 * when `status === 'running'` AND its data is not stale - an idle, paused or stopped clock is static text, always.
 *
 * Nothing here touches React, storage or the network, so it is unit-testable and safe in the headless widget task.
 */

/** Clock states. */
export const CLOCK_STATUS = Object.freeze({
    IDLE: 'idle',
    RUNNING: 'running',
    PAUSED: 'paused',
});

/**
 * A running clock whose server confirmation is older than this is shown FROZEN with a "stale" hint instead of ticking
 * (the widget could not reach the backend, so "still running" is unverified - it must not keep counting on faith).
 */
export const CLOCK_STALE_AFTER_MS = 15 * 60 * 1000;

/** How long a widget tap's optimistic clock state outranks a server answer measured before the tap. */
export const OPTIMISTIC_HOLD_MS = 20 * 1000;

/** Tracker layouts. */
export const TRACKER_LAYOUT = Object.freeze({
    IDLE: 'idle',
    TASK: 'task',
    WORKDAY: 'workday',
    BOTH: 'both',
});

/**
 * @param {*} value ISO string / Date / epoch ms.
 * @returns {number|null} Epoch ms, or null when not a valid date.
 */
export const toMs = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const ms = value instanceof Date ? value.getTime() : (typeof value === 'number' ? value : new Date(value).getTime());

    return Number.isFinite(ms) ? ms : null;
};

/** @param {*} value Any. @returns {number} Non-negative whole seconds. */
const secs = (value) => Math.max(0, Math.floor(Number(value) || 0));

/**
 * The explicit IDLE workday block (no session today / the day was ended).
 * @returns {object} Idle workday.
 */
export const idleWorkday = () => ({
    running: false,
    status: CLOCK_STATUS.IDLE,
    paused: false,
    pausedAt: null,
    breakSec: 0,
    startedAt: null,
    elapsedSec: 0,
    workedSecAtSnapshot: 0,
    reminderAt: null,
    reminderDue: false,
    targetMinutes: 480,
});

/**
 * The explicit IDLE task block (nothing tracked).
 * @returns {object} Idle task.
 */
export const idleTask = () => ({
    status: CLOCK_STATUS.IDLE,
    name: null,
    projectName: null,
    startedAt: null,
    elapsedSecAtSnapshot: 0,
    onBreak: false,
});

/**
 * Resolve a workday block (explicit or legacy) to its status. Legacy blocks (older backend) only carry
 * `running` / `paused`.
 * @param {object|null} workday Snapshot workday block.
 * @returns {'idle'|'running'|'paused'} Status.
 */
export const workdayStatus = (workday) => {
    if (!workday || workday.running !== true) {
        return CLOCK_STATUS.IDLE;
    }

    if (workday.status === CLOCK_STATUS.PAUSED || workday.status === CLOCK_STATUS.RUNNING) {
        return workday.status;
    }

    return workday.paused === true ? CLOCK_STATUS.PAUSED : CLOCK_STATUS.RUNNING;
};

/**
 * Normalize a raw task clock (the backend's `task`) into the explicit shape.
 * @param {object|null} raw Raw `summary.task`.
 * @returns {object|null} Task block, or null when `raw` is not an object.
 */
export const normalizeTaskClock = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const status = [CLOCK_STATUS.RUNNING, CLOCK_STATUS.PAUSED].includes(raw.status) ? raw.status : CLOCK_STATUS.IDLE;
    const startedAt = status === CLOCK_STATUS.RUNNING && toMs(raw.startedAt) !== null ? new Date(toMs(raw.startedAt)).toISOString() : null;

    // A "running" clock with no anchor cannot tick truthfully - treat it as paused (frozen).
    const safeStatus = status === CLOCK_STATUS.RUNNING && !startedAt ? CLOCK_STATUS.PAUSED : status;

    if (safeStatus === CLOCK_STATUS.IDLE) {
        return idleTask();
    }

    return {
        status: safeStatus,
        name: raw.name ? String(raw.name) : null,
        projectName: raw.projectName ? String(raw.projectName) : null,
        startedAt,
        elapsedSecAtSnapshot: secs(raw.elapsedSecAtSnapshot),
        onBreak: raw.onBreak === true,
    };
};

/**
 * Derive the explicit task block from a C1 widget timer (older backend without `summary.task`).
 * @param {object|null} timer Normalized widget timer (`toWidgetTimer` output).
 * @param {number} measuredAtMs Epoch ms the timer data was measured at.
 * @returns {object} Task block (idle when no timer).
 */
export const taskClockFromTimer = (timer, measuredAtMs) => {
    if (!timer || !timer.id) {
        return idleTask();
    }

    const paused = timer.state === 'paused';
    const startedMs = toMs(timer.startedAt);
    const live = !paused && startedMs !== null;
    const elapsed = paused
        ? secs(timer.pausedElapsedSeconds)
        : (live ? secs((measuredAtMs - startedMs) / 1000) : 0);

    return {
        status: live ? CLOCK_STATUS.RUNNING : CLOCK_STATUS.PAUSED,
        name: timer.taskTitle || timer.projectTitle || timer.title || null,
        projectName: timer.taskTitle ? (timer.projectTitle || timer.title || null) : null,
        startedAt: live ? new Date(startedMs).toISOString() : null,
        elapsedSecAtSnapshot: elapsed,
        onBreak: timer.state === 'break' || ['BREAK', 'COFFEE_TIME'].includes(String(timer.segmentType || '').toUpperCase()),
    };
};

/**
 * Whether a clock measured at `clocksAt` is too old to trust as "still running". An UNKNOWN measurement time (a
 * hand-built legacy snapshot - every published snapshot carries `generatedAt`) is not judged stale.
 * @param {*} clocksAt ISO / ms the clocks were measured at.
 * @param {number} nowMs Current epoch ms.
 * @returns {boolean} True when older than {@link CLOCK_STALE_AFTER_MS}.
 */
export const isClockStale = (clocksAt, nowMs) => {
    const at = toMs(clocksAt);

    return at !== null && nowMs - at > CLOCK_STALE_AFTER_MS;
};

/**
 * Derive everything a renderer needs from a snapshot at `nowMs`.
 *
 * `workedSec` / `elapsedSec` are the values to PRINT: a running, fresh clock adds the time since `clocksAt`; an idle,
 * paused or stale clock prints its at-snapshot value unchanged (static). `ticking` is true only for the former.
 *
 * @param {object} snapshot Widget snapshot (`workday`, `task`, `timer`, `clocksAt`, `generatedAt`).
 * @param {number} [nowMs] Current epoch ms.
 * @returns {{
 *   stale: boolean,
 *   clocksAt: (string|null),
 *   workday: { status: string, ticking: boolean, workedSec: number, breakSec: number, startedAt: (string|null), pausedAt: (string|null) },
 *   task: { status: string, ticking: boolean, elapsedSec: number, name: (string|null), projectName: (string|null), onBreak: boolean, startedAt: (string|null) },
 * }} Derived state.
 */
export const deriveTrackerState = (snapshot, nowMs = Date.now()) => {
    const snap = snapshot && typeof snapshot === 'object' ? snapshot : {};
    const clocksAt = snap.clocksAt || snap.generatedAt || null;
    const stale = isClockStale(clocksAt, nowMs);
    const age = clocksAt && toMs(clocksAt) !== null ? Math.max(0, Math.floor((nowMs - toMs(clocksAt)) / 1000)) : 0;

    const wdStatus = workdayStatus(snap.workday);
    const wdBase = secs(snap.workday?.workedSecAtSnapshot ?? snap.workday?.elapsedSec);
    const wdTicking = wdStatus === CLOCK_STATUS.RUNNING && !stale;

    const task = snap.task && typeof snap.task === 'object'
        ? normalizeTaskClock(snap.task)
        : taskClockFromTimer(snap.timer, toMs(clocksAt) ?? nowMs);
    const taskTicking = task.status === CLOCK_STATUS.RUNNING && !stale;
    // A running task is anchored by its own start stamp (exact); everything else is its at-snapshot total.
    const taskElapsed = task.status === CLOCK_STATUS.RUNNING && task.startedAt && !stale
        ? secs((nowMs - toMs(task.startedAt)) / 1000)
        : task.elapsedSecAtSnapshot;

    return {
        stale,
        clocksAt,
        workday: {
            status: wdStatus,
            ticking: wdTicking,
            workedSec: wdTicking ? wdBase + age : wdBase,
            breakSec: secs(snap.workday?.breakSec),
            startedAt: snap.workday?.startedAt || null,
            pausedAt: snap.workday?.pausedAt || null,
        },
        task: {
            status: task.status,
            ticking: taskTicking,
            elapsedSec: taskElapsed,
            name: task.name,
            projectName: task.projectName,
            onBreak: task.onBreak,
            startedAt: task.startedAt,
        },
    };
};

/**
 * Pick the tracker layout from the two clocks and the widget size tier.
 *
 *   none active                  -> `idle`  (clean start screen, never a ticking clock)
 *   only one active              -> that one alone
 *   both active, medium / large  -> `both`  (two rows)
 *   both active, small           -> `task` (the task timer wins) + a small workday chip
 *
 * @param {ReturnType<typeof deriveTrackerState>} state Derived state.
 * @param {'small'|'medium'|'large'} [tier] Size tier.
 * @returns {{ mode: string, workdayChip: boolean }} Layout.
 */
export const selectTrackerLayout = (state, tier = 'medium') => {
    const taskActive = state.task.status !== CLOCK_STATUS.IDLE;
    const workdayActive = state.workday.status !== CLOCK_STATUS.IDLE;

    if (taskActive && workdayActive) {
        return tier === 'small'
            ? { mode: TRACKER_LAYOUT.TASK, workdayChip: true }
            : { mode: TRACKER_LAYOUT.BOTH, workdayChip: false };
    }
    if (taskActive) {
        return { mode: TRACKER_LAYOUT.TASK, workdayChip: false };
    }
    if (workdayActive) {
        return { mode: TRACKER_LAYOUT.WORKDAY, workdayChip: false };
    }

    return { mode: TRACKER_LAYOUT.IDLE, workdayChip: false };
};

/**
 * Pick the authoritative clock blocks between the previously published snapshot and a fresh server answer.
 *
 * The server wins whenever it measured at or after the prior blocks (the normal case). The one exception: the prior
 * blocks were written OPTIMISTICALLY by a widget tap (`optimisticAt`) less than {@link OPTIMISTIC_HOLD_MS} ago and the
 * server answer was measured BEFORE that tap (a cached pre-stop summary racing the tap) - the newer local intent stands
 * so a stopped clock is not resurrected. Once the hold window passes, the server is the truth again (a tap whose backend
 * call failed self-corrects).
 *
 * @param {object|null} prior Previously published snapshot.
 * @param {{ workday: object, task: object, clocksAt: string }} fresh Clock blocks from the server answer.
 * @param {number} [nowMs] Current epoch ms.
 * @returns {{ workday: object, task: object, clocksAt: string, optimisticAt?: string }} Winning blocks.
 */
export const pickAuthoritativeClocks = (prior, fresh, nowMs = Date.now()) => {
    const optimisticMs = toMs(prior?.optimisticAt);
    const freshMs = toMs(fresh.clocksAt);

    if (optimisticMs !== null && nowMs - optimisticMs < OPTIMISTIC_HOLD_MS && (freshMs === null || freshMs < optimisticMs)) {
        return {
            workday: prior.workday || fresh.workday,
            task: prior.task || fresh.task,
            clocksAt: prior.clocksAt || fresh.clocksAt,
            optimisticAt: prior.optimisticAt,
        };
    }

    return { workday: fresh.workday, task: fresh.task, clocksAt: fresh.clocksAt };
};

/**
 * Re-measure the workday block at `nowIso` so a task action does not leave it anchored to an older `clocksAt`.
 * @param {object} day Workday block.
 * @param {ReturnType<typeof deriveTrackerState>} state Derived state.
 * @param {string} nowIso New measurement time.
 * @returns {object} Workday block measured at `nowIso`.
 */
const freezeWorkdayMeasure = (day, state, nowIso) => {
    if (workdayStatus(day) === CLOCK_STATUS.IDLE) {
        return day;
    }

    return { ...day, elapsedSec: state.workday.workedSec, workedSecAtSnapshot: state.workday.workedSec, measuredAt: nowIso };
};

/**
 * Re-measure the task block at `nowIso` (a running task is anchored by `startedAt`, so only its at-snapshot total moves).
 * @param {object} base Snapshot.
 * @param {ReturnType<typeof deriveTrackerState>} state Derived state.
 * @param {string} nowIso New measurement time.
 * @returns {object} Task block.
 */
const taskMeasure = (base, state, nowIso) => {
    if (state.task.status === CLOCK_STATUS.IDLE) {
        return idleTask();
    }

    return {
        status: state.task.status,
        name: state.task.name,
        projectName: state.task.projectName,
        startedAt: state.task.startedAt,
        elapsedSecAtSnapshot: state.task.elapsedSec,
        onBreak: state.task.onBreak,
        measuredAt: nowIso,
    };
};

/** Click actions that mutate a clock, mapped to a human-readable intent. */
export const CLOCK_ACTIONS = Object.freeze([
    'TIMER_PAUSE',
    'TIMER_RESUME',
    'TIMER_STOP',
    'WORKDAY_PAUSE',
    'WORKDAY_RESUME',
    'WORKDAY_STOP',
    'WORKDAY_START',
]);

/**
 * Apply a widget tap to a snapshot IMMEDIATELY (before the network answers) so the home screen reacts at once and a
 * stop can never leave a ticking clock behind. Pure: returns a NEW snapshot; unknown actions return it untouched.
 * The real server answer replaces this on the next refresh (see {@link pickAuthoritativeClocks}).
 *
 * @param {object} snapshot Current snapshot.
 * @param {string} action Click action (`TIMER_*`, `WORKDAY_*`).
 * @param {number} [nowMs] Current epoch ms.
 * @returns {object} Updated snapshot.
 */
export const applyOptimisticClockAction = (snapshot, action, nowMs = Date.now()) => {
    if (!CLOCK_ACTIONS.includes(action)) {
        return snapshot;
    }

    const base = snapshot && typeof snapshot === 'object' ? snapshot : {};
    const state = deriveTrackerState(base, nowMs);
    const nowIso = new Date(nowMs).toISOString();
    const next = { ...base, clocksAt: nowIso, optimisticAt: nowIso };
    const day = base.workday || idleWorkday();

    switch (action) {
        case 'TIMER_PAUSE':
            next.task = { ...state.task, status: CLOCK_STATUS.PAUSED, startedAt: null, elapsedSecAtSnapshot: state.task.elapsedSec };
            next.workday = freezeWorkdayMeasure(day, state, nowIso);
            next.timer = base.timer
                ? { ...base.timer, state: 'paused', startedAt: null, pausedElapsedSeconds: state.task.elapsedSec }
                : base.timer;
            break;
        case 'TIMER_RESUME':
            next.task = { ...state.task, status: CLOCK_STATUS.RUNNING, startedAt: nowIso, elapsedSecAtSnapshot: 0, onBreak: false };
            next.workday = freezeWorkdayMeasure(day, state, nowIso);
            next.timer = base.timer ? { ...base.timer, state: 'running', startedAt: nowIso, pausedElapsedSeconds: null } : base.timer;
            break;
        case 'TIMER_STOP':
            next.task = idleTask();
            next.timer = null;
            next.workday = freezeWorkdayMeasure(day, state, nowIso);
            break;
        case 'WORKDAY_PAUSE':
            next.workday = {
                ...day,
                running: true,
                status: CLOCK_STATUS.PAUSED,
                paused: true,
                pausedAt: nowIso,
                elapsedSec: state.workday.workedSec,
                workedSecAtSnapshot: state.workday.workedSec,
            };
            next.task = taskMeasure(base, state, nowIso);
            break;
        case 'WORKDAY_RESUME': {
            const pausedMs = toMs(day.pausedAt);
            const extraBreak = pausedMs === null ? 0 : secs((nowMs - pausedMs) / 1000);

            next.workday = {
                ...day,
                running: true,
                status: CLOCK_STATUS.RUNNING,
                paused: false,
                pausedAt: null,
                breakSec: secs(day.breakSec) + extraBreak,
                elapsedSec: state.workday.workedSec,
                workedSecAtSnapshot: state.workday.workedSec,
            };
            next.task = taskMeasure(base, state, nowIso);
            break;
        }
        case 'WORKDAY_START':
            next.workday = {
                ...idleWorkday(),
                running: true,
                status: CLOCK_STATUS.RUNNING,
                startedAt: nowIso,
            };
            next.task = taskMeasure(base, state, nowIso);
            break;
        case 'WORKDAY_STOP':
        default:
            next.workday = idleWorkday();
            next.task = taskMeasure(base, state, nowIso);
            break;
    }

    return next;
};
